import React, { useState, useRef, useEffect, forwardRef } from 'react'
import { ChangeResult, IMultiRangeSliderProps } from './types'
import sharedStyles from '@muiscss/sharedStyles.module.scss'
import styles from './styles.module.scss'
import clsx from 'clsx'

let _triggerTimeout: number | null = null

export const MultiRangeSlider = forwardRef<HTMLDivElement, IMultiRangeSliderProps>((props, ref) => {
  const {
    label,
    disabled,
    min,
    max,
    step = 1,
    scale,
    size = 'md',
    value,
    maxValue,
    id,
    className,
    tooltipPrefix,
    tooltipSuffix,
    hasTooltips,
    hasMarkers,
    hasSubMarkers,
    onChange,
    onFocus,
  } = props

  const [minVal, setMinVal] = useState(value)
  const [maxVal, setMaxVal] = useState(maxValue)
  const [barMin, setBarMin] = useState(((minVal - min) / (max - min)) * 100)
  const [barMax, setBarMax] = useState(((max - maxVal) / (max - min)) * 100)
  const [isChange, setIsChange] = useState(true)
  const refBar = useRef<HTMLDivElement>(null)

  const onBarLeftClick = () => {
    if (disabled) return
    let _minValue = minVal - step
    if (_minValue < min) {
      _minValue = min
    }
    setMinVal(_minValue)
  }

  const onInputMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    let _minValue = parseFloat(e.target.value)
    if (_minValue > maxVal - step) {
      _minValue = maxVal - step
    }
    setMinVal(_minValue)
    setIsChange(true)
  }
  const onLeftThumbMousedown: React.MouseEventHandler = (e: React.MouseEvent) => {
    if (disabled) return
    const startX = e.clientX
    const thumb = e.target as HTMLDivElement
    const bar = thumb.parentNode as HTMLDivElement
    const barBox = bar.getBoundingClientRect()
    const barValue = minVal
    setIsChange(false)
    const onLeftThumbMousemove: { (e: MouseEvent): void } = (e: MouseEvent) => {
      const clientX = e.clientX
      const dx = clientX - startX
      const per = dx / barBox.width
      let val = barValue + (max - min) * per
      val = Math.round(val / step) * step
      val = parseFloat(val.toFixed(0))
      if (val < min) {
        val = min
      } else if (val > maxVal - step) {
        val = maxVal - step
      }
      setMinVal(val)
    }
    const onLeftThumbMouseup: { (e: MouseEvent): void } = () => {
      setIsChange(true)
      document.removeEventListener('mousemove', onLeftThumbMousemove)
      document.removeEventListener('mouseup', onLeftThumbMouseup)
    }
    document.addEventListener('mousemove', onLeftThumbMousemove)
    document.addEventListener('mouseup', onLeftThumbMouseup)
  }
  const onLeftThumbTouchStart = (e: React.TouchEvent) => {
    if (disabled) return
    const startX = e.touches[0].clientX
    const thumb = e.target as HTMLDivElement
    const bar = thumb.parentNode as HTMLDivElement
    const barBox = bar.getBoundingClientRect()
    const barValue = minVal
    setIsChange(false)
    const onLeftThumbToucheMove: { (e: TouchEvent): void } = (e: TouchEvent) => {
      const clientX = e.touches[0].clientX
      const dx = clientX - startX
      const per = dx / barBox.width
      let val = barValue + (max - min) * per
      val = Math.round(val / step) * step
      val = parseFloat(val.toFixed(0))
      if (val < min) {
        val = min
      } else if (val > maxVal - step) {
        val = maxVal - step
      }
      setMinVal(val)
    }
    const onLeftThumbTouchEnd: { (e: TouchEvent): void } = () => {
      setIsChange(true)
      document.removeEventListener('touchmove', onLeftThumbToucheMove)
      document.removeEventListener('touchend', onLeftThumbTouchEnd)
    }

    document.addEventListener('touchmove', onLeftThumbToucheMove)
    document.addEventListener('touchend', onLeftThumbTouchEnd)
  }
  const onInnerBarLeftClick = () => {
    if (disabled) return
    let _minValue = minVal + step
    if (_minValue > maxVal - step) {
      _minValue = maxVal - step
    }
    setMinVal(_minValue)
  }
  const onInnerBarRightClick = () => {
    if (disabled) return
    let _maxValue = maxVal - step
    if (_maxValue < minVal + step) {
      _maxValue = minVal + step
    }
    setMaxVal(_maxValue)
  }
  const onInputMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    let _maxValue = parseFloat(e.target.value)
    if (_maxValue < minVal + step) {
      _maxValue = minVal + step
    }
    setMaxVal(_maxValue)
    setIsChange(true)
  }
  const onRightThumbMousedown: React.MouseEventHandler = (e: React.MouseEvent) => {
    if (disabled) return
    const startX = e.clientX
    const thumb = e.target as HTMLDivElement
    const bar = thumb.parentNode as HTMLDivElement
    const barBox = bar.getBoundingClientRect()
    const barValue = maxVal
    setIsChange(false)
    const onRightThumbMousemove: { (e: MouseEvent): void } = (e: MouseEvent) => {
      const clientX = e.clientX
      const dx = clientX - startX
      const per = dx / barBox.width
      let val = barValue + (max - min) * per
      val = Math.round(val / step) * step
      val = parseFloat(val.toFixed(0))
      if (val < minVal + step) {
        val = minVal + step
      } else if (val > max) {
        val = max
      }
      setMaxVal(val)
    }
    const onRightThumbMouseup: { (e: MouseEvent): void } = () => {
      setIsChange(true)
      document.removeEventListener('mousemove', onRightThumbMousemove)
      document.removeEventListener('mouseup', onRightThumbMouseup)
    }
    document.addEventListener('mousemove', onRightThumbMousemove)
    document.addEventListener('mouseup', onRightThumbMouseup)
  }
  const onRightThumbTouchStart = (e: React.TouchEvent) => {
    if (disabled) return
    const startX = e.touches[0].clientX
    const thumb = e.target as HTMLDivElement
    const bar = thumb.parentNode as HTMLDivElement
    const barBox = bar.getBoundingClientRect()
    const barValue = maxVal
    setIsChange(false)
    const onRightThumbTouchMove: { (e: TouchEvent): void } = (e: TouchEvent) => {
      const clientX = e.touches[0].clientX
      const dx = clientX - startX
      const per = dx / barBox.width
      let val = barValue + (max - min) * per
      val = Math.round(val / step) * step
      val = parseFloat(val.toFixed(0))
      if (val < minVal + step) {
        val = minVal + step
      } else if (val > max) {
        val = max
      }
      setMaxVal(val)
    }
    const onRightThumbTouchEnd: { (e: TouchEvent): void } = () => {
      setIsChange(true)
      document.removeEventListener('touchmove', onRightThumbTouchMove)
      document.removeEventListener('touchend', onRightThumbTouchEnd)
    }
    document.addEventListener('touchmove', onRightThumbTouchMove)
    document.addEventListener('touchend', onRightThumbTouchEnd)
  }
  const onBarRightClick = () => {
    if (disabled) return
    let _maxValue = maxVal + step
    if (_maxValue > max) {
      _maxValue = max
    }
    setMaxVal(_maxValue)
  }
  useEffect(() => {
    if (refBar && refBar.current) {
      const bar = refBar.current as HTMLDivElement
      const p_bar = bar.parentNode as HTMLDivElement
      p_bar.addEventListener('wheel', (e) => {
        if (!e.shiftKey && !e.ctrlKey) {
          return
        }
        e.preventDefault()
      })
    }
  }, [refBar])

  useEffect(() => {
    if (maxVal < minVal) {
      console.error('maxValue is less than minValue')
      // show error state here if needed
    }
    const triggerChange = () => {
      const result: ChangeResult = { min: minVal, max: maxVal }
      isChange && onChange && onChange(result)
      onFocus && onFocus(result)
    }
    const _barMin = ((minVal - min) / (max - min)) * 100
    setBarMin(_barMin)
    const _barMax = ((max - maxVal) / (max - min)) * 100
    setBarMax(_barMax)
    _triggerTimeout && window.clearTimeout(_triggerTimeout)
    _triggerTimeout = window.setTimeout(triggerChange, 20)
  }, [minVal, maxVal, min, max, onChange, onFocus, isChange, hasTooltips])

  useEffect(() => {
    let _minValue = value
    if (_minValue < min) {
      _minValue = min
    }
    if (_minValue > max) {
      _minValue = max
    }
    setIsChange(false)
    setMinVal(_minValue)
  }, [value, min, max])
  useEffect(() => {
    let _maxValue = maxVal

    if (_maxValue > max) {
      _maxValue = max
    }
    if (_maxValue < min) {
      _maxValue = min
    }
    setIsChange(false)
    setMaxVal(_maxValue)
  }, [maxVal, min, max, step])

  return (
    <div
      ref={ref}
      id={id}
      className={clsx(
        styles.multiRangeSlider,
        styles[size],
        { [sharedStyles.disabled]: disabled },
        className,
      )}
    >
      {label && (
        <label
          className={clsx(sharedStyles.label, styles.inputLabel)}
          data-testid="mui-label"
          htmlFor={id}
        >
          {label}
        </label>
      )}
      <div className={clsx(styles.bar, styles[size])} ref={refBar}>
        <div
          className={clsx(styles.barLeft, styles[size])}
          style={{ width: barMin + '%' }}
          onClick={onBarLeftClick}
        ></div>
        <input
          placeholder="min-value"
          className={clsx(styles.inputRange, styles.inputRangeMin)}
          type="range"
          min={min}
          max={max}
          step={step}
          value={minVal}
          onInput={onInputMinChange}
        />
        <div
          className={clsx(styles.thumb, styles.thumbLeft, styles[size])}
          onMouseDown={onLeftThumbMousedown}
          onTouchStart={onLeftThumbTouchStart}
        >
          {hasTooltips && (
            <div className={clsx(styles.caption, styles[size])}>
              <span className={clsx(styles.minCaption)}>
                {tooltipPrefix}
                {minVal}
                {tooltipSuffix}
              </span>
            </div>
          )}
        </div>
        <div className={clsx(styles.barInner)}>
          <div className={clsx(styles.barInnerLeft)} onClick={onInnerBarLeftClick}></div>
          <div className={clsx(styles.barInnerRight)} onClick={onInnerBarRightClick}></div>
        </div>
        <input
          placeholder="max-value"
          className={clsx(styles.inputRange, styles.inputRangeMax)}
          type="range"
          min={min}
          max={max}
          step={step}
          value={maxVal}
          onInput={onInputMaxChange}
        />
        <div
          className={clsx(styles.thumb, styles.thumbRight, styles[size])}
          onMouseDown={onRightThumbMousedown}
          onTouchStart={onRightThumbTouchStart}
        >
          {hasTooltips && (
            <div className={clsx(styles.caption, styles[size])}>
              <span className={clsx(styles.maxCaption)}>
                {tooltipPrefix}
                {maxVal}
                {tooltipSuffix}
              </span>
            </div>
          )}
        </div>
        <div
          className={clsx(styles.barRight)}
          style={{ width: barMax + '%' }}
          onClick={onBarRightClick}
        ></div>
      </div>

      {scale && (
        <div className={clsx(styles.scale, styles[size])}>
          {scale.map((scaleLabel, index) => {
            const isLastItem = index === scale.length - 1
            return (
              <>
                <div key={scaleLabel.toString()} className={clsx(styles.scaleLabel)}>
                  {hasMarkers && <div className={clsx(styles.marker)}></div>}
                  <span>{scaleLabel}</span>
                </div>
                {!isLastItem && hasSubMarkers && (
                  <div
                    key={scaleLabel.toString()}
                    className={clsx(styles.scaleLabel, styles.subScaleLabel)}
                  >
                    {hasMarkers && (
                      <div className={clsx(styles.marker)}>
                        <div className={clsx(styles.subMarker)}></div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )
          })}
        </div>
      )}
    </div>
  )
})
